// components/CalculatorForm.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Divider,
  Box,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Business,
  AccessTime,
  AttachMoney,
  Calculate,
  PictureAsPdf,
  Save,
  Cancel
} from '@mui/icons-material';
import CompanyInfoSection from './CompanyInfoSection';
import PercentageSection from './PercentageSection';
import ResultsSection from './ResultsSection';
import FinalAmountAdjuster from './FinalAmountAdjuster';
import SummarySection from './SummarySection';
import { axiosInstance } from '../../utils/axios';
import {generatePDF} from '../../utils/pdfGenerator';

const IVA_RATE = 0.21;

const CalculatorForm = ({ editingCalculation = null, onSaveComplete = null, onCancel = null }) => {
  const [formData, setFormData] = useState({
    nombreEmpresa: '',
    horasServicio: '',
    montoBase: '',
    porcentajeSep: '',
    porcentajeOct: '',
    porcentajeNov: '',
    porcentajeDic: '',
    porcentajeEne: '',
    clienteId: null
  });

  const [calculatedData, setCalculatedData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showIVA, setShowIVA] = useState(true);
  const [montoFinalDeseado, setMontoFinalDeseado] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [savedCalculationId, setSavedCalculationId] = useState(null);
  const [savingCalculation, setSavingCalculation] = useState(false);

  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Función para mostrar notificaciones
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Función para limpiar el formulario
  const clearForm = () => {
    setFormData({
      nombreEmpresa: '',
      horasServicio: '',
      montoBase: '',
      porcentajeSep: '',
      porcentajeOct: '',
      porcentajeNov: '',
      porcentajeDic: '',
      porcentajeEne: '',
      clienteId: null
    });
    setCalculatedData(null);
    setShowResults(false);
    setMontoFinalDeseado('');
    setSavedCalculationId(null);
  };

  // Función para cargar datos de edición
  const loadEditingData = (calculation) => {
    setFormData({
      nombreEmpresa: calculation.nombre_empresa || '',
      horasServicio: calculation.horas_servicio?.toString() || '',
      montoBase: calculation.monto_base?.toString() || '',
      porcentajeSep: calculation.porcentaje_sep?.toString() || '',
      porcentajeOct: calculation.porcentaje_oct?.toString() || '',
      porcentajeNov: calculation.porcentaje_nov?.toString() || '',
      porcentajeDic: calculation.porcentaje_dic?.toString() || '',
      porcentajeEne: calculation.porcentaje_ene?.toString() || '',
      clienteId: calculation.cliente_id
    });
    setSavedCalculationId(calculation.id);
    
    // Auto-calcular con los datos cargados
    setTimeout(() => {
      calcular();
    }, 100);
  };

  // Efecto para cargar datos cuando se está editando
  useEffect(() => {
    if (editingCalculation) {
      loadEditingData(editingCalculation);
    } else {
      clearForm();
    }
  }, [editingCalculation]);

  // Función para calcular aumentos
  const calcular = () => {
    const { nombreEmpresa, horasServicio, montoBase, porcentajeSep, porcentajeOct, porcentajeNov, porcentajeDic, porcentajeEne } = formData;
    
    const montoBaseNum = parseFloat(montoBase) || 0;
    const sepNum = parseFloat(porcentajeSep) || 0;
    const octNum = parseFloat(porcentajeOct) || 0;
    const novNum = parseFloat(porcentajeNov) || 0;
    const dicNum = parseFloat(porcentajeDic) || 0;
    const eneNum = parseFloat(porcentajeEne) || 0;

    if (!montoBaseNum || montoBaseNum <= 0) {
      setCalculatedData(null);
      setShowResults(false);
      return;
    }

    // Cálculos progresivos (sin IVA)
    const montoSep = montoBaseNum * (1 + sepNum / 100);
    const montoOct = montoBaseNum * (1 + (sepNum + octNum) / 100);
    const montoNov = montoBaseNum * (1 + (sepNum + octNum + novNum) / 100);
    const montoDic = montoBaseNum * (1 + (sepNum + octNum + novNum + dicNum) / 100);
    const montoEne = montoBaseNum * (1 + (sepNum + octNum + novNum + dicNum + eneNum) / 100);

    // Porcentajes acumulados
    const porcentajeAcumSep = sepNum;
    const porcentajeAcumOct = sepNum + octNum;
    const porcentajeAcumNov = sepNum + octNum + novNum;
    const porcentajeAcumDic = sepNum + octNum + novNum + dicNum;
    const porcentajeAcumEne = sepNum + octNum + novNum + dicNum + eneNum;

    const calculated = {
      nombreEmpresa,
      horasServicio,
      montoBase: montoBaseNum,
      porcentajeSep: sepNum,
      porcentajeOct: octNum,
      porcentajeNov: novNum,
      porcentajeDic: dicNum,
      porcentajeEne: eneNum,
      montoSep,
      montoOct,
      montoNov,
      montoDic,
      montoEne,
      porcentajeAcumSep,
      porcentajeAcumOct,
      porcentajeAcumNov,
      porcentajeAcumDic,
      porcentajeAcumEne,
      clienteId: formData.clienteId
    };

    setCalculatedData(calculated);
    setMontoFinalDeseado(montoEne.toFixed(2));
    setShowResults(true);
    
    // Si estamos editando, no resetear el saved ID
    if (!editingCalculation) {
      setSavedCalculationId(null);
    }
  };

  // Función para guardar el cálculo en la base de datos
  const saveCalculationToDatabase = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      throw new Error('No hay datos de cálculo para guardar');
    }

    if (!formData.clienteId) {
      throw new Error('Debe seleccionar un cliente');
    }

    try {
      const dataToSave = {
        cliente_id: formData.clienteId,
        nombre_empresa: calculatedData.nombreEmpresa,
        horas_servicio: parseFloat(calculatedData.horasServicio) || 0,
        monto_base: calculatedData.montoBase,
        porcentaje_sep: calculatedData.porcentajeSep,
        porcentaje_oct: calculatedData.porcentajeOct,
        porcentaje_nov: calculatedData.porcentajeNov,
        porcentaje_dic: calculatedData.porcentajeDic,
        porcentaje_ene: calculatedData.porcentajeEne
      };

      let response;
      
      if (editingCalculation && savedCalculationId) {
        // Actualizar cálculo existente
        response = await axiosInstance.put(`/calculo/${savedCalculationId}`, dataToSave);
        showSnackbar('Cálculo actualizado exitosamente', 'success');
      } else {
        // Crear nuevo cálculo
        response = await axiosInstance.post('/calculo', dataToSave);
        setSavedCalculationId(response.data.id);
        showSnackbar('Cálculo guardado exitosamente', 'success');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al guardar cálculo:', error);
      
      // En caso de error de conexión, simular guardado local
      if (error.code === 'NETWORK_ERROR') {
        const localId = savedCalculationId || `local_${Date.now()}`;
        setSavedCalculationId(localId);
        showSnackbar('Cálculo guardado localmente (sin conexión a API)', 'warning');
        return { id: localId };
      }
      
      throw error;
    }
  };

  // Función para guardar solo el cálculo (sin PDF)
  const handleSaveCalculation = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      showSnackbar('Por favor realice primero el cálculo antes de guardar', 'error');
      return;
    }

    if (!formData.clienteId) {
      showSnackbar('Debe seleccionar un cliente para guardar el cálculo', 'error');
      return;
    }

    setSavingCalculation(true);

    try {
      const savedData = await saveCalculationToDatabase();
      
      // Si es un callback de edición, notificar al componente padre
      if (onSaveComplete) {
        onSaveComplete(savedData);
      }
      
      // Si es un nuevo cálculo, limpiar el formulario
      if (!editingCalculation) {
        setTimeout(() => {
          clearForm();
          showSnackbar('Formulario limpiado para nuevo cálculo', 'info');
        }, 2000);
      }
      
    } catch (error) {
      if (error.message.includes('cliente')) {
        showSnackbar('Error: Debe seleccionar un cliente válido', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Error: Datos inválidos. Verifique la información', 'error');
      } else {
        showSnackbar('Error al guardar el cálculo', 'error');
      }
    } finally {
      setSavingCalculation(false);
    }
  };

  // Función mejorada para generar PDF que también guarda en BD
  const handleGeneratePDF = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      showSnackbar('Por favor realice primero el cálculo antes de generar el PDF', 'error');
      return;
    }

    if (!formData.clienteId) {
      showSnackbar('Debe seleccionar un cliente para generar el PDF', 'error');
      return;
    }

    setGeneratingPDF(true);

    try {
      // 1. Primero guardar en la base de datos
      showSnackbar('Guardando cálculo en la base de datos...', 'info');
      const savedData = await saveCalculationToDatabase();
      
      // 2. Luego generar el PDF
      showSnackbar('Generando PDF...', 'info');
      await generatePDF(calculatedData, () => {});
      
      // 3. Mostrar confirmación de éxito
      showSnackbar(
        `PDF generado y cálculo guardado exitosamente (ID: ${savedData.id})`, 
        'success'
      );

      // 4. Si es callback de edición, notificar al componente padre
      if (onSaveComplete) {
        onSaveComplete(savedData);
      }
      
      // 5. Si es un nuevo cálculo, limpiar el formulario después de un tiempo
      if (!editingCalculation) {
        setTimeout(() => {
          clearForm();
          showSnackbar('Formulario limpiado para nuevo cálculo', 'info');
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error en el proceso:', error);
      
      if (error.message.includes('cliente')) {
        showSnackbar('Error: Debe seleccionar un cliente válido', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Error: Datos inválidos. Verifique la información', 'error');
      } else {
        // Si falla el guardado pero queremos generar PDF de todas formas
        try {
          await generatePDF(calculatedData, () => {});
          showSnackbar('PDF generado, pero no se pudo guardar en la base de datos', 'warning');
        } catch (pdfError) {
          showSnackbar('Error al generar el PDF', 'error');
        }
      }
    } finally {
      setGeneratingPDF(false);
    }
  };

  const ajustarMontoFinal = () => {
    const montoFinalDeseadoNum = parseFloat(montoFinalDeseado);
    
    if (!montoFinalDeseadoNum || montoFinalDeseadoNum <= calculatedData.montoBase) {
      showSnackbar('Por favor ingrese un monto final válido (mayor al monto base)', 'error');
      return;
    }

    // Calcular el nuevo porcentaje total necesario
    const nuevoPorcentajeTotal = ((montoFinalDeseadoNum - calculatedData.montoBase) / calculatedData.montoBase) * 100;
    
    // Mantener las proporciones originales de los porcentajes
    const porcentajeOriginalTotal = calculatedData.porcentajeAcumEne;
    const factor = nuevoPorcentajeTotal / porcentajeOriginalTotal;
    
    // Calcular nuevos porcentajes manteniendo proporciones
    const nuevoSep = calculatedData.porcentajeSep * factor;
    const nuevoOct = calculatedData.porcentajeOct * factor;
    const nuevoNov = calculatedData.porcentajeNov * factor;
    const nuevoDic = calculatedData.porcentajeDic * factor;
    const nuevoEne = calculatedData.porcentajeEne * factor;

    // Actualizar formData
    setFormData(prev => ({
      ...prev,
      porcentajeSep: nuevoSep.toFixed(2),
      porcentajeOct: nuevoOct.toFixed(2),
      porcentajeNov: nuevoNov.toFixed(2),
      porcentajeDic: nuevoDic.toFixed(2),
      porcentajeEne: nuevoEne.toFixed(2)
    }));

    showSnackbar('Porcentajes ajustados. Recalcule para ver los nuevos resultados.', 'info');
  };

  const isEditMode = !!editingCalculation;

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {isEditMode ? '✏️ Editar Cálculo' : '💰 Calculadora de Aumentos Progresivos'}
          </Typography>
          
          {isEditMode && onCancel && (
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={onCancel}
              color="inherit"
            >
              Cancelar Edición
            </Button>
          )}
        </Box>

        {isEditMode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Editando cálculo ID: {savedCalculationId}. Los cambios se guardarán cuando presione "Guardar" o "Generar PDF".
          </Alert>
        )}
        
        <CompanyInfoSection formData={formData} setFormData={setFormData} />

        {/* Explicación */}
        <Card sx={{ mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 ¿Cómo funcionan los aumentos progresivos?
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Los porcentajes se acumulan mes a mes:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>Septiembre:</strong> Monto base + % septiembre<br/>
              • <strong>Octubre:</strong> Monto base + % septiembre + % octubre<br/>
              • <strong>Noviembre:</strong> Monto base + % septiembre + % octubre + % noviembre<br/>
              • <strong>Y así sucesivamente...</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Ejemplo: Si pones 10% cada mes, en diciembre tendrás un 40% de aumento total.
            </Typography>
          </CardContent>
        </Card>

        <PercentageSection formData={formData} setFormData={setFormData} />

        <Button
          variant="contained"
          size="large"
          startIcon={<Calculate />}
          onClick={calcular}
          sx={{ mt: 3 }}
        >
          🔢 {isEditMode ? 'Recalcular' : 'Calcular Aumentos'}
        </Button>
      </Paper>

      {/* Resultados */}
      {showResults && calculatedData && (
        <>
          <ResultsSection 
            calculatedData={calculatedData} 
            showIVA={showIVA} 
            setShowIVA={setShowIVA}
            IVA_RATE={IVA_RATE}
          />

          <FinalAmountAdjuster 
            montoFinalDeseado={montoFinalDeseado}
            setMontoFinalDeseado={setMontoFinalDeseado}
            ajustarMontoFinal={ajustarMontoFinal}
          />

          <SummarySection calculatedData={calculatedData} IVA_RATE={IVA_RATE} />

          {/* Botones de acción */}
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={savingCalculation ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSaveCalculation}
                  disabled={savingCalculation || !formData.clienteId}
                  sx={{ minWidth: 200 }}
                >
                  {savingCalculation ? 'Guardando...' : (isEditMode ? '💾 Actualizar Cálculo' : '💾 Guardar Cálculo')}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                  onClick={handleGeneratePDF}
                  disabled={generatingPDF || !formData.clienteId}
                  sx={{ minWidth: 200 }}
                >
                  {generatingPDF ? 'Generando PDF...' : '📄 Generar PDF'}
                </Button>
              </Box>

              {/* Alertas de estado */}
              {!formData.clienteId && (
                <Alert severity="warning">
                  Debe seleccionar un cliente para poder guardar el cálculo y generar PDF
                </Alert>
              )}

              {savedCalculationId && !isEditMode && (
                <Alert severity="success">
                  ✅ Cálculo guardado exitosamente en la base de datos (ID: {savedCalculationId})
                </Alert>
              )}

              {!isEditMode && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  💡 Después de guardar o generar PDF, el formulario se limpiará automáticamente
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CalculatorForm;